import React, { useState, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, AlertCircle, X, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import adminService from "../services/adminService";
import { formatError } from "../utils/errorHandler";
import useCustomToast from "../hooks/useCustomToast";
import AuthContext from "../context/AuthContext";
import { useTranslation, useFormat } from "../i18n";

// Predefined rejection reasons. The id is stable UI state; the label (looked up
// through t()) is what gets sent to the backend as the rejection reason.
const REJECTION_REASONS = [
  { id: "insufficient_budget", key: "reject_dialog.reasons.insufficient_budget" },
  { id: "policy", key: "reject_dialog.reasons.policy" },
  { id: "not_eligible", key: "reject_dialog.reasons.not_eligible" },
  { id: "duplicate", key: "reject_dialog.reasons.duplicate" },
  { id: "incomplete", key: "reject_dialog.reasons.incomplete" },
  { id: "other", key: "reject_dialog.reasons.other" },
];
const OTHER_REASON_ID = "other";

// Recognition memos are free text and can run to several sentences. Rendered in
// full they stretched the approvals table until it scrolled sideways, so each
// one is clamped to a preview with an inline toggle.
const MEMO_PREVIEW_LIMIT = 90;

const RequestMemo = ({ memo, expanded, onToggle, t, className = "" }) => {
  const isTruncatable = memo.length > MEMO_PREVIEW_LIMIT;
  const shown =
    expanded || !isTruncatable ? memo : `${memo.slice(0, MEMO_PREVIEW_LIMIT).trimEnd()}…`;

  return (
    <p className={className}>
      <span className="whitespace-pre-wrap break-words">{shown}</span>
      {isTruncatable && (
        <button
          type="button"
          onClick={onToggle}
          className="ml-1 font-medium text-purple-600 hover:text-purple-800 hover:underline"
        >
          {expanded ? t("common:actions.show_less") : t("common:actions.show_more")}
        </button>
      )}
    </p>
  );
};

// Substitute React nodes (e.g. profile links) for {{placeholders}} left in a
// translated sentence, so links survive translation without splitting the text.
const withNodes = (template, nodes) =>
  String(template)
    .split(/(\{\{\s*\w+\s*\}\})/g)
    .map((part, index) => {
      const match = part.match(/^\{\{\s*(\w+)\s*\}\}$/);
      if (!match) return part;
      const node = nodes[match[1]];
      return node === undefined ? part : <React.Fragment key={index}>{node}</React.Fragment>;
    });

const ApprovalsPage = () => {
  const { t } = useTranslation("approvals");
  const fmt = useFormat();
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const isSuperuserOrAdmin = user?.is_superuser || user?.role === "admin";
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectMemo, setRejectMemo] = useState("");
  const [expandedMemos, setExpandedMemos] = useState(() => new Set());

  const toggleMemo = (rowKey) =>
    setExpandedMemos((previous) => {
      const next = new Set(previous);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });

  // Fetch pending requests (backend returns enriched data with names)
  const { data: requests = [], isLoading: requestsLoading, error: requestsError, refetch } = useQuery({
    queryKey: ["pending-approvals"],
    queryFn: () => adminService.getPendingApprovals(),
  });

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (requestId) => adminService.approveTransfer(requestId),
    onSuccess: () => {
      toast.success(t("toasts.approved"));
      queryClient.invalidateQueries({ queryKey: ["pending-approvals"] });
    },
    onError: (error) => {
      toast.error(t("toasts.approve_failed", { error: formatError(error) }));
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ requestId, reason }) => adminService.rejectTransfer(requestId, reason),
    onSuccess: () => {
      toast.success(t("toasts.rejected"));
      closeRejectDialog();
      queryClient.invalidateQueries({ queryKey: ["pending-approvals"] });
    },
    onError: (error) => {
      toast.error(t("toasts.reject_failed", { error: formatError(error) }));
    },
  });

  const handleApprove = (request) => {
    if (!request.id) return;
    approveMutation.mutate(request.id);
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason("");
    setRejectMemo("");
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setSelectedRequest(null);
    setRejectDialogOpen(false);
    setRejectReason("");
    setRejectMemo("");
  };

  const handleReject = () => {
    if (!selectedRequest || !selectedRequest.id) return;

    // Combine reason and custom memo
    const selectedReason = REJECTION_REASONS.find((reason) => reason.id === rejectReason);
    const reasonText = selectedReason ? t(selectedReason.key) : "";
    let fullReason = reasonText;
    if (rejectMemo.trim()) {
      fullReason = reasonText ? `${reasonText}\n${rejectMemo}` : rejectMemo;
    }

    rejectMutation.mutate({
      requestId: selectedRequest.id,
      reason: fullReason || null,
    });
  };

  const formatCreatedAt = (value) => (value ? fmt.dateTime(value) : "");

  const sourceLabelFor = (source) =>
    source === 'recognition' ? t("source.recognition") :
    source === 'nomination' ? t("source.nomination") :
    source || t("source.transfer");

  const pointsPillFor = (request) =>
    request.batch_size > 1
      ? t("points_pill_batch", { amount: fmt.number(request.amount), batch: fmt.number(request.batch_size) })
      : t("points_pill", { amount: fmt.number(request.amount) });

  return (
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Refresh button */}
        <div className="mb-2 sm:mb-6 flex items-center justify-end">
          <button
            onClick={() => refetch()}
            className="p-1 sm:p-2 hover:bg-gray-100 rounded-2xl transition-colors"
            title={t("refresh")}
          >
            <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
          </button>
        </div>

        {/* Error */}
        {requestsError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl">
            <p className="text-sm text-red-700">
              {formatError(requestsError)}
            </p>
          </div>
        )}

        {/* Content */}
        {requestsLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("empty.title")}</h3>
            <p className="text-gray-600">
              {t("empty.body")}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("columns.from_to")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("columns.points")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("columns.source")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("columns.team")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[180px]">
                        {t("columns.submitted")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("columns.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {requests.map((request) => {
                      const senderName = request.sender_name || request.sender_id;
                      const recipientName = request.recipient_name || request.recipient_id;
                      const teamName = request.team_name;
                      const createdAt = formatCreatedAt(request.created_at);
                      const isProcessing = approveMutation.isPending || rejectMutation.isPending;
                      const rowKey = request.id ?? `${request.sender_id}-${request.recipient_id}`;

                      return (
                        <tr
                          key={rowKey}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              <p className="font-medium text-gray-900 truncate max-w-[220px]">
                                <Link to={`/profile/${request.sender_id}`} className="hover:text-purple-600 transition">
                                  {senderName}
                                </Link>
                              </p>
                              <p className="text-sm text-gray-500 max-w-[220px]">
                                →{" "}
                                <Link to={`/profile/${request.recipient_id}`} className="hover:text-purple-600 transition">
                                  {recipientName}
                                </Link>
                                {request.co_recipient_names && request.co_recipient_names.length > 0 && (
                                  <span className="text-gray-400">
                                    {", "}
                                    {request.co_recipient_names.slice(0, 2).join(", ")}
                                    {request.co_recipient_names.length > 2 && ` ${t("more_recipients", { more: request.co_recipient_names.length - 2 })}`}
                                  </span>
                                )}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {pointsPillFor(request)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request.source === 'recognition'
                                ? 'bg-purple-100 text-purple-800'
                                : request.source === 'nomination'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {sourceLabelFor(request.source)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">
                              {teamName ? teamName : request.team_id ? request.team_id : "-"}
                            </span>
                          </td>
                          <td className="px-6 py-4 align-top">
                            <div className="space-y-1 max-w-[280px]">
                              <p className="text-sm text-gray-900 whitespace-nowrap">{createdAt}</p>
                              {request.memo && (
                                <RequestMemo
                                  memo={request.memo}
                                  expanded={expandedMemos.has(rowKey)}
                                  onToggle={() => toggleMemo(rowKey)}
                                  t={t}
                                  className="text-sm text-gray-500"
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-2 flex-wrap">
                              <button
                                onClick={() => handleApprove(request)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {isProcessing ? (
                                  <>
                                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                                    {t("actions.approving")}
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="w-4 h-4" />
                                    {t("common:actions.approve")}
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => openRejectDialog(request)}
                                disabled={isProcessing}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-600 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <XCircle className="w-4 h-4" />
                                {t("common:actions.reject")}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile stacked cards */}
            <div className="sm:hidden space-y-3">
              {requests.map((request) => {
                const senderName = request.sender_name || request.sender_id;
                const recipientName = request.recipient_name || request.recipient_id;
                const teamName = request.team_name;
                const createdAt = formatCreatedAt(request.created_at);
                const isProcessing = approveMutation.isPending || rejectMutation.isPending;
                const rowKey = request.id ?? `${request.sender_id}-${request.recipient_id}`;
                const sourceLabel = sourceLabelFor(request.source);
                const sourceClass = request.source === 'recognition'
                  ? 'bg-purple-100 text-purple-800'
                  : request.source === 'nomination'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-gray-100 text-gray-800';

                return (
                  <div
                    key={rowKey}
                    className="bg-white rounded-2xl shadow-md p-3 space-y-2.5"
                  >
                    {/* Row 1: recipient bold + points pill */}
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/profile/${request.recipient_id}`} className="text-sm font-semibold text-gray-900 hover:text-purple-600 truncate min-w-0 flex-1">
                        {recipientName}
                        {request.co_recipient_names && request.co_recipient_names.length > 0 && (
                          <span className="text-gray-400 font-normal">
                            {", "}
                            {request.co_recipient_names.slice(0, 2).join(", ")}
                            {request.co_recipient_names.length > 2 && ` ${t("more_recipients", { more: request.co_recipient_names.length - 2 })}`}
                          </span>
                        )}
                      </Link>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 flex-shrink-0">
                        {pointsPillFor(request)}
                      </span>
                    </div>

                    {/* Row 2: → sender + date/time on the right */}
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <p className="text-gray-500 truncate min-w-0 flex-1">
                        →{" "}
                        <Link to={`/profile/${request.sender_id}`} className="hover:text-purple-600">
                          {senderName}
                        </Link>
                      </p>
                      <span className="text-gray-500 flex-shrink-0 whitespace-nowrap">{createdAt}</span>
                    </div>

                    {/* Row 3: badges */}
                    <div className="flex items-center flex-wrap gap-1.5 text-xs">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${sourceClass}`}>
                        {sourceLabel}
                      </span>
                      {teamName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {teamName}
                        </span>
                      )}
                    </div>

                    {request.memo && (
                      <RequestMemo
                        memo={request.memo}
                        expanded={expandedMemos.has(rowKey)}
                        onToggle={() => toggleMemo(rowKey)}
                        t={t}
                        className="text-xs text-gray-600 bg-gray-50 rounded-lg px-2 py-1.5"
                      />
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(request)}
                        disabled={isProcessing}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                            {t("actions.approving_short")}
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            {t("common:actions.approve")}
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => openRejectDialog(request)}
                        disabled={isProcessing}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-red-600 border border-red-600 hover:bg-red-50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                        {t("common:actions.reject")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Reject Dialog */}
        <AnimatePresence>
          {rejectDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={closeRejectDialog}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-full">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t("reject_dialog.title")}
                    </h2>
                  </div>
                  <button
                    onClick={closeRejectDialog}
                    className="p-1 hover:bg-gray-100 rounded-2xl transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <p className="text-sm text-gray-600">
                    {selectedRequest ? (
                      withNodes(
                        t(
                          selectedRequest.co_recipient_names?.length > 0
                            ? "reject_dialog.summary_with_others"
                            : "reject_dialog.summary",
                          {
                            others: t("reject_dialog.others", { count: selectedRequest.co_recipient_names?.length || 0 }),
                            points:
                              selectedRequest.batch_size > 1
                                ? t("reject_dialog.points_batch_recipients", {
                                    amount: fmt.number(selectedRequest.amount),
                                    batch: fmt.number(selectedRequest.batch_size),
                                  })
                                : t("points_pill", { amount: fmt.number(selectedRequest.amount) }),
                          }
                        ),
                        {
                          sender: (
                            <Link to={`/profile/${selectedRequest.sender_id}`} className="font-medium text-purple-600 hover:text-purple-800">
                              {selectedRequest.sender_name || selectedRequest.sender_id}
                            </Link>
                          ),
                          recipient: (
                            <Link to={`/profile/${selectedRequest.recipient_id}`} className="font-medium text-purple-600 hover:text-purple-800">
                              {selectedRequest.recipient_name || selectedRequest.recipient_id}
                            </Link>
                          ),
                        }
                      )
                    ) : (
                      t("reject_dialog.select_request")
                    )}
                  </p>

                  {/* Predefined Reason Dropdown */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("reject_dialog.reason_label")}
                    </label>
                    <select
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">{t("reject_dialog.reason_placeholder")}</option>
                      {REJECTION_REASONS.map((reason) => (
                        <option key={reason.id} value={reason.id}>
                          {t(reason.key)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Message */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t("reject_dialog.notes_label")} {rejectReason === OTHER_REASON_ID && <span className="text-red-500">*</span>}
                    </label>
                    <textarea
                      value={rejectMemo}
                      onChange={(e) => setRejectMemo(e.target.value)}
                      placeholder={t("reject_dialog.notes_placeholder")}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <button
                    onClick={closeRejectDialog}
                    disabled={rejectMutation.isPending}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t("common:actions.cancel")}
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={
                      rejectMutation.isPending ||
                      !rejectReason ||
                      (rejectReason === OTHER_REASON_ID && !rejectMemo.trim())
                    }
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {rejectMutation.isPending ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        {t("reject_dialog.rejecting")}
                      </span>
                    ) : (
                      t("reject_dialog.reject_request")
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ApprovalsPage;
